import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft, AlertCircle, RefreshCw, Trash2, Briefcase,
  FileText, MessageSquare, GraduationCap, Scale, FileSignature,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { jobsApi } from '../services/api';
import { ResearchJob } from '../types';
import { ResearchProgress } from '../components/research/ResearchProgress';
import { ResearchResults } from '../components/research/ResearchResults';
import { CasePicker } from '../components/cases/CasePicker';
import { ChatPanel } from '../components/studio/ChatPanel';
import { MemoQuiz } from '../components/studio/MemoQuiz';
import { DraftsPanel } from '../components/studio/DraftsPanel';
import { useMattersStore } from '../store/mattersStore';

type StudioTab = 'memo' | 'chat' | 'quiz' | 'debate' | 'drafts';

/** Internal tab key → URL segment (§4.3): /app/research/:id/{memo|discuss|quiz|opposing|drafts}. */
const TAB_SEGMENT: Record<StudioTab, string> = {
  memo: 'memo',
  chat: 'discuss',
  quiz: 'quiz',
  debate: 'opposing',
  drafts: 'drafts',
};

/** URL segment (or legacy `state.tab` key) → internal tab key. */
const SEGMENT_TO_TAB: Record<string, StudioTab> = {
  memo: 'memo',
  discuss: 'chat',
  quiz: 'quiz',
  opposing: 'debate',
  drafts: 'drafts',
  // Legacy state.tab values (pre-subroute deep links) still resolve.
  chat: 'chat',
  debate: 'debate',
};

const STUDIO_TABS: { key: StudioTab; label: string; icon: typeof FileText }[] = [
  { key: 'memo', label: 'Memo', icon: FileText },
  { key: 'chat', label: 'Discuss', icon: MessageSquare },
  { key: 'quiz', label: 'Test me', icon: GraduationCap },
  { key: 'debate', label: 'Opposing counsel', icon: Scale },
  { key: 'drafts', label: 'Drafts', icon: FileSignature },
];

/** One-line teaching captions, shown the first time each tab is visited (§4.3, P7). */
const TAB_CAPTIONS: Record<StudioTab, string> = {
  memo: 'Your full research memorandum — download it as PDF or Word below.',
  chat: 'Ask follow-up questions about this memo — answers stay grounded in its authorities.',
  quiz: 'Quick questions from the memo, so you know you have it down before the hearing.',
  debate: 'Argue against your own memo before the other side does.',
  drafts: 'Turn this memo into a court-ready draft that cites its verified authorities.',
};

/** Muted one-liner under the tab row on a tab's first-ever visit (localStorage-flagged). */
function FirstVisitCaption({ tab }: { tab: StudioTab }) {
  const storageKey = `urava_tab_caption_${tab}`;
  const [visible] = useState(() => {
    try {
      return localStorage.getItem(storageKey) !== '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      /* private mode — caption just shows every time */
    }
  }, [storageKey]);
  if (!visible) return null;
  return <p className="text-xs text-legal-400 mb-3">{TAB_CAPTIONS[tab]}</p>;
}

const isTerminal = (s?: string) => s === 'completed' || s === 'failed';

export function JobDetail() {
  const { id, tab: tabParam } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // A freshly-created job is handed over via router state so we can render the
  // progress UI immediately (no blank skeleton while the first fetch runs).
  // Legacy deep links can still request a tab via state.tab (redirected below).
  const routeState = location.state as { job?: ResearchJob; tab?: string } | null;
  const seeded = routeState?.job;
  // Tabs are URL-driven: /app/research/:id/{memo|discuss|quiz|opposing|drafts}.
  const tab: StudioTab = (tabParam && SEGMENT_TO_TAB[tabParam]) || 'memo';
  const [job, setJob] = useState<ResearchJob | null>(seeded ?? null);
  const [isLoading, setIsLoading] = useState(!seeded);
  const [error, setError] = useState('');
  // "File under a case" nudge (IA, July 2026): when the memo is unfiled, the
  // breadcrumb slot offers a quiet chip that opens the case picker in place.
  const [showFileUnder, setShowFileUnder] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Case breadcrumb (§4.3): resolve the linked case's title from the store cache.
  const matters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  useEffect(() => {
    fetchMatters();
  }, [fetchMatters]);
  const linkedMatter = job?.matter_id ? matters.find((m) => m.id === job.matter_id) : undefined;

  const selectTab = (t: StudioTab) => {
    if (t === tab) return;
    navigate(`/app/research/${id}/${TAB_SEGMENT[t]}`);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Full fetch — includes the (large) memo blob. Used on mount for an existing
  // job and exactly once when a running job reaches a terminal state.
  const fetchFullJob = async (): Promise<ResearchJob | null> => {
    if (!id) return null;
    try {
      const { data } = await jobsApi.getJob(id);
      setJob(data);
      return data;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load job');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Lightweight poll — status/progress only, never transfers the memo.
  const pollProgress = async () => {
    if (!id) return;
    try {
      const { data } = await jobsApi.getJobProgress(id);
      if (isTerminal(data.status)) {
        stopPolling();
        await fetchFullJob(); // hydrate the memo once, now that it's done
      } else {
        setJob((prev) =>
          prev
            ? { ...prev, status: data.status, progress_percent: data.progress_percent, current_step: data.current_step }
            : prev,
        );
      }
    } catch {
      /* transient poll error — keep the interval running */
    }
  };

  useEffect(() => {
    let active = true;
    stopPolling();

    if (seeded && !isTerminal(seeded.status)) {
      // Fresh job already in hand — render it and just poll progress (light).
      setIsLoading(false);
      pollRef.current = setInterval(pollProgress, 4000);
    } else {
      // Existing/direct open: fetch the full job (memo if completed); poll only
      // if it's still running.
      setIsLoading(!seeded);
      (async () => {
        const data = await fetchFullJob();
        if (!active || !data) return;
        if (!isTerminal(data.status)) {
          pollRef.current = setInterval(pollProgress, 4000);
        }
      })();
    }

    return () => {
      active = false;
      stopPolling();
    };
  }, [id]);

  // Optimistic case assignment via the existing PATCH /jobs/{id} — the job's
  // documents travel with it server-side.
  const handleAssignCase = async (matterId: string | null) => {
    if (!job) return;
    const prev = job.matter_id ?? null;
    setJob({ ...job, matter_id: matterId });
    if (matterId) setShowFileUnder(false);
    try {
      await jobsApi.assignMatter(job.id, matterId);
    } catch {
      setJob((j) => (j ? { ...j, matter_id: prev } : j));
    }
  };

  const handleDelete = async () => {
    if (!job || !confirm('Delete this research job?')) return;
    try {
      await jobsApi.deleteJob(job.id);
      navigate('/app');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete job');
    }
  };

  // Legacy deep links (`state.tab`) redirect to the canonical subroute (§4.0),
  // carrying the seeded job so the handoff render stays instant.
  const legacyTab = !tabParam && routeState?.tab ? SEGMENT_TO_TAB[routeState.tab] : undefined;
  if (legacyTab && legacyTab !== 'memo' && id) {
    return (
      <Navigate
        to={`/app/research/${id}/${TAB_SEGMENT[legacyTab]}`}
        replace
        state={seeded ? { job: seeded } : undefined}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-legal-100 rounded w-1/3" />
          <div className="h-4 bg-legal-100 rounded w-2/3" />
          <div className="h-48 bg-legal-100 rounded" />
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error || 'Job not found'}</p>
        </div>
        <Link to="/app" className="mt-4 inline-flex items-center space-x-2 text-legal-500 hover:text-legal-700">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Today</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-legal-500 hover:text-legal-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Back</span>
            </button>
            {job.matter_id ? (
              <Link
                to={`/app/cases/${job.matter_id}`}
                data-track="memo-case-breadcrumb"
                className="inline-flex items-center gap-1.5 text-xs text-legal-600 bg-legal-50 border border-legal-200 rounded-full px-2.5 py-1 hover:border-accent hover:text-accent-700 transition-colors max-w-[16rem]"
              >
                <Briefcase className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{linkedMatter?.title ?? 'View case file'}</span>
              </Link>
            ) : showFileUnder ? (
              <CasePicker compact value={null} onChange={handleAssignCase} />
            ) : (
              <button
                type="button"
                onClick={() => setShowFileUnder(true)}
                data-track="memo-file-under-case"
                className="inline-flex items-center gap-1.5 text-xs text-legal-500 bg-white border border-dashed border-legal-300 rounded-full px-2.5 py-1 hover:border-accent hover:text-accent-700 transition-colors"
              >
                <Briefcase className="h-3 w-3 flex-shrink-0" />
                <span>File under a case</span>
              </button>
            )}
          </div>
          <h1 className="text-2xl font-bold text-legal-900">{job.title}</h1>
          <div className="flex items-center space-x-3 mt-2">
            {job.jurisdiction && (
              <span className="text-xs bg-legal-100 text-legal-600 px-2 py-1 rounded-full">
                {job.jurisdiction}
              </span>
            )}
            {job.law_domain && (
              <span className="text-xs bg-accent-50 text-accent-700 px-2 py-1 rounded-full">
                {job.law_domain}
              </span>
            )}
            <span className="text-xs text-legal-400">
              Created {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
            </span>
            <CasePicker compact value={job.matter_id ?? null} onChange={handleAssignCase} />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {(job.status === 'completed' || job.status === 'failed') && (
            <button
              onClick={handleDelete}
              className="p-2 text-legal-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete job"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={fetchFullJob}
            className="p-2 text-legal-400 hover:text-legal-700 hover:bg-legal-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Research question */}
      <div className="bg-legal-50 rounded-lg p-4 mb-6 border border-legal-200">
        <p className="text-sm font-medium text-legal-500 mb-1">Research Question</p>
        <p className="text-legal-800">{job.description}</p>
      </div>

      {/* Status-based content */}
      {(job.status === 'pending' || job.status === 'processing') && (
        <ResearchProgress
          progress={job.progress_percent}
          currentStep={job.current_step}
          status={job.status}
        />
      )}

      {job.status === 'completed' && job.research_output_md && (
        <div>
          {/* Memo tabs — URL-driven (§4.3) */}
          <div
            data-tour="studio-tabs"
            className="flex items-center space-x-1 mb-4 bg-legal-100 p-1 rounded-lg w-fit max-w-full overflow-x-auto"
          >
            {STUDIO_TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => selectTab(key)}
                data-track={`studio-tab-${TAB_SEGMENT[key]}`}
                {...(key === 'drafts' ? { 'data-tour': 'memo-drafts-tab' } : {})}
                className={`flex items-center space-x-1.5 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  tab === key
                    ? 'bg-white text-legal-900 shadow-sm'
                    : 'text-legal-500 hover:text-legal-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {/* One-line teaching caption on a tab's first visit (P7) */}
          <FirstVisitCaption key={tab} tab={tab} />

          {tab === 'memo' && (
            <>
              <ResearchResults job={job} />
              {/* "What next" strip (§4.3) — the forward pipeline under the memo */}
              <div className="mt-4 bg-white border border-legal-200 rounded-lg p-4">
                <p className="text-[11px] uppercase tracking-[0.14em] text-legal-400 mb-2.5">
                  What next
                </p>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      { key: 'drafts' as StudioTab, label: 'Draft from this memo', icon: FileSignature },
                      { key: 'chat' as StudioTab, label: 'Discuss it', icon: MessageSquare },
                      { key: 'quiz' as StudioTab, label: 'Test me', icon: GraduationCap },
                      { key: 'debate' as StudioTab, label: 'Opposing counsel', icon: Scale },
                    ]
                  ).map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => selectTab(key)}
                      data-track={`memo-what-next-${TAB_SEGMENT[key]}`}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-legal-300 rounded-lg text-sm font-medium text-legal-700 hover:border-accent hover:text-accent-700 hover:bg-accent-50 transition-colors"
                    >
                      <Icon className="h-4 w-4" />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          {tab === 'chat' && <ChatPanel jobId={job.id} mode="chat" />}
          {tab === 'quiz' && <MemoQuiz jobId={job.id} />}
          {tab === 'debate' && <ChatPanel jobId={job.id} mode="debate" />}
          {tab === 'drafts' && <DraftsPanel jobId={job.id} jobTitle={job.title} />}
        </div>
      )}

      {job.status === 'failed' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-3">
            <AlertCircle className="h-6 w-6 text-red-500" />
            <h3 className="font-semibold text-red-800">This research failed</h3>
          </div>
          <p className="text-red-700 text-sm mb-4">
            Something went wrong while researching this question — usually a temporary
            issue on our side.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/app/research/new"
              state={{ prefill: job.description, matterId: job.matter_id ?? undefined }}
              data-track="failed-job-try-again"
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
            >
              Try again (won't count against your quota)
            </Link>
            <Link
              to="/feedback"
              data-track="failed-job-report-problem"
              className="text-sm text-red-700 underline underline-offset-2 hover:text-red-900 transition-colors"
            >
              Report a problem
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
